import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const chapters = [
  {
    n: "01",
    tag: "The problem",
    title: "You already paid for these leads.",
    body: "Every dormant contact in your CRM cost you money to acquire — an ad click, a walk-in, a referral. Then life happened, follow-up slipped, and they went cold. That's not a dead list. That's prepaid pipeline.",
  },
  {
    n: "02",
    tag: "The shift",
    title: "Reactivation beats acquisition.",
    body: "Winning back someone who already knows you is faster, cheaper, and warmer than chasing a stranger. The right message at the right moment turns silence into booked revenue — without inflating your ad spend.",
  },
  {
    n: "03",
    tag: "The promise",
    title: "We carry the risk. You keep the upside.",
    body: "No retainers. No setup fees. No software to learn. We run the entire reactivation, in your voice, with your approval — and we only get paid a share of the revenue we actually recover.",
  },
];

function Chapter({ c }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.85", "start 0.35"],
  });
  const opacity = useTransform(scrollYProgress, [0, 1], [0.25, 1]);
  const x = useTransform(scrollYProgress, [0, 1], [-24, 0]);

  return (
    <motion.article
      ref={ref}
      style={{ opacity }}
      data-testid={`manifesto-${c.n}`}
      className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 py-12 lg:py-20 border-t border-white/10"
    >
      <div className="lg:col-span-4 flex items-start gap-5">
        <span className="font-display text-5xl lg:text-7xl text-[#ccff00] leading-none">
          {c.n}
        </span>
        <span className="mt-2 text-[10px] uppercase tracking-[0.3em] text-white/40">
          {c.tag}
        </span>
      </div>
      <motion.div style={{ x }} className="lg:col-span-8 max-w-2xl">
        <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase leading-[0.95] mb-5">
          {c.title}
        </h3>
        <p className="text-white/60 text-base lg:text-lg leading-relaxed">
          {c.body}
        </p>
      </motion.div>
    </motion.article>
  );
}

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative py-24 lg:py-32 bg-[#050505]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-xs uppercase tracking-[0.25em] text-[#ccff00] font-semibold mb-4">
          / The manifesto
        </div>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase max-w-3xl mb-4">
          Why the smartest revenue is the revenue you forgot.
        </h2>
        <div>
          {chapters.map((c) => (
            <Chapter key={c.n} c={c} />
          ))}
        </div>
      </div>
    </section>
  );
}
